import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useServiceChecklists, useServiceVisits, useStartServiceVisit } from '../api/queries'
import type { Device, ServiceVisit } from '../api/types'
import { formatDate, formatDateTime } from '../lib/format'
import { Card } from './ui'

/**
 * Servicedelen af enhedens side: aftalen, knappen der starter et besøg, og
 * historikken.
 *
 * Knappen står øverst og er stor: den bliver trykket af en montør, der lige har
 * scannet mærkatet og står med telefonen i den ene hånd
 * (docs/DECISIONS.md beslutning 026).
 */
export function DeviceServiceCard({ device, due }: { device: Device; due: { label: string; background: string; color: string } | null }) {
  const navigate = useNavigate()
  const { data: visits } = useServiceVisits(device.id)
  const { data: checklists } = useServiceChecklists(device.deviceType)
  const start = useStartServiceVisit()

  const [checklistId, setChecklistId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const open = visits?.find((v) => v.status === 'in_progress')

  async function startVisit() {
    setError(null)
    try {
      const visit = await start.mutateAsync({
        deviceId: device.id,
        checklistId: checklistId ? Number(checklistId) : null
      })
      navigate(`/service-visits/${visit.visit.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Servicebesøget kunne ikke startes.')
    }
  }

  return (
    <>
      <Card title="Service">
        {device.serviceIntervalMonths ? (
          <div className="form-grid" style={{ marginBottom: 14 }}>
            <div>
              <div className="tiny muted">Interval</div>
              <div className="small">Hver {device.serviceIntervalMonths}. måned</div>
            </div>
            <div>
              <div className="tiny muted">Næste service</div>
              <div className="small">
                {formatDate(device.nextServiceDate)}
                {due && (
                  <span className="pill" style={{ marginLeft: 8, background: due.background, color: due.color }}>
                    {due.label}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="tiny muted">Sidste service</div>
              <div className="small">{formatDate(device.lastServiceDate) || 'Aldrig serviceret'}</div>
            </div>
          </div>
        ) : (
          <div className="muted small" style={{ marginBottom: 14 }}>
            Enheden er ikke på serviceaftale. Servicen kan stadig udføres og dokumenteres.
          </div>
        )}

        {open ? (
          <div className="stack" style={{ gap: 8 }}>
            <div className="small">
              Der er et servicebesøg i gang, startet {formatDateTime(open.startedAt)}
              {open.performedBy ? ` af ${open.performedBy.displayName}` : ''}.
            </div>
            <div>
              <Link className="btn btn--primary" to={`/service-visits/${open.id}`}>
                Fortsæt service
              </Link>
            </div>
          </div>
        ) : (
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <button className="btn btn--primary" disabled={start.isPending} onClick={startVisit}>
              {start.isPending ? 'Starter ...' : 'Start service'}
            </button>

            {/* Systemet vælger selv listen ud fra enhedstypen; her kan den overstyres. */}
            {checklists && checklists.length > 1 && (
              <select
                style={{ maxWidth: 240 }}
                value={checklistId}
                onChange={(event) => setChecklistId(event.target.value)}
              >
                <option value="">Foreslået checkliste</option>
                {checklists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name}
                  </option>
                ))}
              </select>
            )}

            {checklists && checklists.length === 0 && (
              <span className="tiny muted">
                Der er ingen checkliste til denne enhedstype - besøget bliver uden punkter.
              </span>
            )}
          </div>
        )}

        {error && <div className="error-box" style={{ marginTop: 12 }}>{error}</div>}
      </Card>
    </>
  )
}

/**
 * Alle besøg på enheden, nyeste først - også de afbrudte.
 *
 * Står for sig selv og NEDERST på enhedens side: sammenstillingen ovenfor
 * (DeviceHistoryCard, beslutning 062) svarer på det, montøren spørger om, mens
 * han står der. Listen her er til, når man skal finde en bestemt rapport frem.
 */
export function DeviceVisitsCard({ deviceId }: { deviceId: number }) {
  const { data: visits } = useServiceVisits(deviceId)
  const history = (visits ?? []).filter((v) => v.status !== 'in_progress')

  if (history.length === 0) return null

  return (
    <Card title={`Alle servicebesøg (${history.length})`}>
      <div className="stack">
        {history.map((visit) => (
          <HistoryRow key={visit.id} visit={visit} />
        ))}
      </div>
    </Card>
  )
}

function HistoryRow({ visit }: { visit: ServiceVisit }) {
  const cancelled = visit.status === 'cancelled'

  return (
    <Link className="row row--between device-row" to={`/service-visits/${visit.id}`}>
      <div style={{ minWidth: 0 }}>
        <div className="small">
          {formatDate(visit.completedAt ?? visit.startedAt)}
          {visit.performedBy ? ` · ${visit.performedBy.displayName}` : ''}
        </div>
        <div className="tiny muted">{visit.checklistName ?? 'Uden checkliste'}</div>
      </div>

      <div style={{ whiteSpace: 'nowrap' }}>
        {cancelled ? (
          <span className="pill" style={{ background: '#F1F5F9', color: '#64748B' }}>
            Afbrudt
          </span>
        ) : visit.deviationCount > 0 ? (
          <span className="pill" style={{ background: '#FEF3C7', color: '#92400E' }}>
            {visit.deviationCount} afvigelse{visit.deviationCount === 1 ? '' : 'r'}
          </span>
        ) : (
          <span className="pill" style={{ background: '#DCFCE7', color: '#166534' }}>
            Uden afvigelser
          </span>
        )}
      </div>
    </Link>
  )
}
